import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {  BehaviorSubject, map, Observable, lastValueFrom } from 'rxjs';
import { getAuth, signOut, signInWithEmailAndPassword, sendPasswordResetEmail, User, onAuthStateChanged, Auth, createUserWithEmailAndPassword, IdTokenResult } from "firebase/auth";
import { ActiveCompanyService } from '../../shared/services/active-company.service';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CustomUser } from '../../shared/interfaces/custom-user.interface';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private redirectUrl: string = '';
  user$: CustomUser | null = null;
  private createUserUrl = 'https://us-central1-consentimientos-informados.cloudfunctions.net/createUserV2';
  private setUserRolesUrl = 'https://setuserrolesv2-oa66z4esya-uc.a.run.app';
  private deleteUserUrl = 'https://deleteuser-oa66z4esya-uc.a.run.app';
  private deactivateUserUrl = 'https://us-central1-consentimientos-informados.cloudfunctions.net/deactivateUser';
  private toggleUserStatusUrl = 'https://us-central1-consentimientos-informados.cloudfunctions.net/toggleUserStatus';
  private authStatus = new BehaviorSubject<User | null>(null);

  private auth: Auth;

  // Añadir esta nueva propiedad para almacenar el usuario con roles
  private currentUserWithRoles = new BehaviorSubject<CustomUser | null>(null);

  constructor(private router: Router, private activeCompanyService: ActiveCompanyService, private firestore: AngularFirestore, private http: HttpClient) {
    this.auth = getAuth();
    onAuthStateChanged(this.auth, (user) => {
      this.authStatus.next(user);
      
      // Actualizar el usuario con roles cuando cambie el estado de autenticación
      if (user) {
        this.updateUserWithRoles(user);
      } else {
        this.currentUserWithRoles.next(null);
      }
    });

  }

    // Método para obtener la instancia de Auth
    getAuth(): Auth {
      return this.auth;
    }

  setRedirectUrl(url: string) {
    this.redirectUrl = url;
  }

  getRedirectUrl() {
    return this.redirectUrl;
  }

  async login(email: string, password: string): Promise<boolean | Error> {
    this.activeCompanyService.loading = true;
    const auth = getAuth();
    
    try {
      // Autenticación de usuario con email y contraseña
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
  
      // Obtener los roles del usuario desde Firestore
      const userDoc = await this.firestore.collection('companies')
        .doc(this.activeCompanyService.activeCompanyId ?? 'de0bd083-8325-4de0-83b7-772ccd7c354f')
        .collection('users')
        .doc(user.uid)
        .get().toPromise();
  
      let roles: string[] = [];
  
      if (userDoc!.exists) {
        const userData = userDoc!.data();
        if (userData) {
          roles = userData['roles'] ?? [];
          this.user$ = userData as CustomUser;
          this.user$.roles = roles;
        }
      }
  
      // Si no se encuentran roles en Firestore, obtener roles desde los claims del token de ID
      if (roles.length === 0) {
        const idTokenResult = await user.getIdTokenResult();  // Obtener el token de ID y sus claims
        const claims = idTokenResult.claims;
  
        // Verificar si los roles están en los claims
        if (claims['roles']) {
          roles = claims['roles'] as string[];
        }
      }
  
      // Obtener el token de ID para guardarlo en sessionStorage
      const token = await this.getIdToken();
  
      // Guardar solo el email, roles y token en SessionStorage
      const userSessionData = {
        email: user.email,
        roles: roles,
        token: token,
        displayName: user.displayName || user.email
      };
      
      sessionStorage.setItem('currentUser', JSON.stringify(userSessionData));
  
      // Actualizar el usuario con roles
      await this.updateUserWithRoles(user);
      
      this.activeCompanyService.loading = false;
      // No navegamos aquí, dejamos que el componente lo haga
      
      // Importante: devolver para que la promesa se resuelva correctamente
      return true;
    } catch (error) {
      this.activeCompanyService.loading = false;
      // Importante: lanzar el error para que pueda ser capturado por el componente
      throw error;
    }
  }
  

  async logout() {
    this.activeCompanyService.loading = true;
    const auth = getAuth();
    try {
      // Primero hacer signOut
      await signOut(auth);
      
      // Limpiar todas las formas de almacenamiento en orden
      // 1. SessionStorage
      sessionStorage.clear(); // Limpia todo el sessionStorage
      
      // 2. LocalStorage específico de Firebase
      Object.keys(localStorage).forEach(key => {
        if (key.includes('firebase:') || key.includes('firebaseui:')) {
          localStorage.removeItem(key);
        }
      });
      
      // 3. IndexedDB de Firebase
      await this.clearFirebaseIndexedDB();
      
      this.activeCompanyService.loading = false;
      this.router.navigateByUrl('/login');
    } catch (error) {
      console.error('Logout error:', error);
      this.activeCompanyService.loading = false;
      // Intentar limpiar todo incluso si hay error
      sessionStorage.clear();
      localStorage.clear();
      await this.clearFirebaseIndexedDB();
      this.router.navigateByUrl('/login');
    }
  }

  isLoggedIn(): boolean{
    return this.authStatus.value !== null;
    // const auth = getAuth();
    // return auth.currentUser !== null; 
  }

  getAuthStatus(): Observable<User | null> {
    return this.authStatus.asObservable();
  }

  getUserInfo(){
      const auth = getAuth();
      this.user$ = auth.currentUser;
      return this.user$; 
  }

  // Método para crear usuario usando Cloud Function (sin iniciar sesión automáticamente)
  async createUserViaCloudFunction(
    email: string, 
    displayName: string, 
    password: string, 
    roles: string[], 
    companyId: string,
    identificationType?: { value: string; label: string } | null,
    identificationNumber?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
        const token = await this.getIdToken();
        const headers = new HttpHeaders({
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        });

        const body = {
          email,
          password,
          roles,
          companyId,
          displayName
        };

        console.log('Enviando datos a createUserV2:', body);
        console.log('identificationType recibido:', identificationType);
        console.log('identificationNumber recibido:', identificationNumber);

        // Llamar a la Cloud Function createUser v2
        const response = await this.http.post(this.createUserUrl, body, { headers }).toPromise();
        
        console.log('Respuesta de createUserV2:', response);
        
        if (response && (response as any).success) {
          const responseData = response as any;
          const uid = responseData.user?.uid;
          
          if (uid) {
            // Actualizar Firestore con datos adicionales que no maneja la Cloud Function
            const additionalData: any = {};
            
            if (identificationType && identificationType.value) {
              additionalData.identification = {
                type: identificationType.value,
                typeLabel: identificationType.label,
                number: identificationNumber || ''
              };
            } else {
              additionalData.identification = {
                type: 'CC',
                typeLabel: 'Cédula de ciudadanía',
                number: '111111'
              };
            }

            // Solo actualizar si hay datos adicionales
            if (Object.keys(additionalData).length > 0) {
              await this.firestore.collection('companies').doc(companyId).collection('users').doc(uid).set(additionalData, { merge: true });
            }
          }

          return { success: true, message: 'Usuario creado exitosamente.' };
        } else {
          return { success: false, message: (response as any)?.message || 'Error al crear el usuario.' };
        }
    } catch (error) {
        console.error('Error al crear usuario via Cloud Function:', error);
        return { success: false, message: 'Error al crear el usuario.' };
    }
  }

  // Método de registro de usuarios usando cloudFunctions
  async register(
    email: string, 
    displayName: string, 
    password: string, 
    roles: string[], 
    companyId: string,
    identificationType?: { value: string; label: string } | null,
    identificationNumber?: string
  ): Promise<{ success: boolean; message: string }> {
    try {
        // Crear el usuario con Firebase Authentication
        const userCredential = await createUserWithEmailAndPassword(getAuth(), email, password);
        const uid = userCredential.user.uid;

        // Crear el objeto del usuario para Firestore con todos los campos necesarios
        const userData: any = {
          email,
          displayName,
          roles,
          isActive: true,
          emailVerified: false,
          metadata: {
            creationTime: new Date().toISOString()
          }
        };
        if (identificationType) {
          userData.identification = {
            type: identificationType.value,
            typeLabel: identificationType.label,
            number: identificationNumber || ''
          };
        } else {
          userData.identification = {
            type: 'CI',
            typeLabel: 'Cédula identidad',
            number: '111111'
          };
        }

        // Guardar el usuario en Firestore directamente
        await this.firestore.collection('companies').doc(companyId).collection('users').doc(uid).set(userData);

        // Actualizar roles y demás información
        const updateResult = await this.updateUserAndRoles(uid, roles, companyId, displayName, email, identificationType, identificationNumber);
        
        // Esperar a que se complete la actualización de roles
        await lastValueFrom(updateResult);

        // NOTA: No desconectamos al usuario recién creado para evitar problemas de sesión
        // El usuario que está creando la empresa deberá hacer logout manualmente si es necesario

        return { success: true, message: 'Usuario creado exitosamente.' };
    } catch (error) {
        console.error('Error al registrar usuario:', error);
        return { success: false, message: 'Error al crear el usuario.' };
    }
  }

 
async getIdToken(): Promise<string> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (user) {
    return await user.getIdToken(/* forceRefresh */ true); // Esto asegura que el token sea válido
  }
  throw new Error('Usuario no autenticado');
}

// Función para obtener todos los usuarios de una compañía dada por su companyId
getUsersByCompanyId(companyId: string): Observable<any[]> {
  return this.firestore
    .collection(`companies/${companyId}/users`)
    .snapshotChanges()
    .pipe(
      map(actions => 
        actions.map(a => {
          const data = a.payload.doc.data() as CustomUser || {};
          const id = a.payload.doc.id;
          const roles = data.roles || []; // Asegurarnos de que roles sea un array

          // Crear un objeto que contiene las propiedades para cada rol
          const roleFlags = {
            company_admin: roles.includes('company_admin') || false,
            company_user: roles.includes('company_user') || false,
            company_medical_staff: roles.includes('company_medical_staff') || false,
            company_administrative_staff: roles.includes('company_administrative_staff') || false,
          };

          return { 
            id, 
            displayName: data.displayName,
            email: data.email,
            roles: roles, // Incluir el array de roles original
            identification: data.identification,
            isActive: data.isActive ?? true,
            ...roleFlags 
          };
        })
      )
    );
}
  
 // Método para asignar roles a un usuario usando la API REST
 async updateUserAndRoles(
   uid: string, 
   roles: string[], 
   companyId: string, 
   displayName: string, 
   email: string,
   identificationType?: { value: string; label: string } | null,
   identificationNumber?: string
 ): Promise<Observable<any>> {
  const token = await this.getIdToken();
  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  // Asegurarse de que displayName no sea undefined o null
  const safeDisplayName = displayName || '';
  
  const body = { 
    uid, 
    roles, 
    companyId, 
    displayName: safeDisplayName,
    email,
    isActive: true,
    identification: identificationType ? {
      type: identificationType.value,
      typeLabel: identificationType.label,
      number: identificationNumber || ''
    } : undefined
  };

  console.log('Updating user with data:', body);

  try {
    // Usar set con merge:true en lugar de update para crear el documento si no existe
    await this.firestore.collection('companies').doc(companyId).collection('users').doc(uid).set({
      roles: roles,
      displayName: safeDisplayName,
      email: email,
      isActive: true,
      identification: identificationType ? {
        type: identificationType.value,
        typeLabel: identificationType.label,
        number: identificationNumber || ''
      } : undefined
    }, { merge: true });
    
    return this.http.post(this.setUserRolesUrl, body, { headers });
  } catch (error) {
    console.error('Error updating user in Firestore:', error);
    throw error;
  }
}

// Método para borrar un usuario de firebase authentication
async deleteUser(uid: string, companyId: string) {
  const token = await this.getIdToken();
  const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

  return this.http.delete(this.deleteUserUrl, {
    headers,
    body: { uid, companyId }
  }).toPromise();
}

// metodo para solicitar el correo de restablecer contraseña
async getPasswordResetEmail(email: string): Promise<void> {
  const auth = getAuth();
  try {
    await sendPasswordResetEmail(auth, email);
    console.log('Password reset email sent successfully');
  } catch (error) {
    console.error('Error sending password reset email:', error);
  }
}

// Método para desactivar un usuario
async deactivateUser(uid: string, companyId: string) {
  const token = await this.getIdToken();
  const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

  return this.http.post(this.deactivateUserUrl, 
    { uid, companyId },
    { headers }
  ).toPromise();
}

// Método para activar o desactivar un usuario
async toggleUserStatus(uid: string, companyId: string, isActive: boolean) {
  const token = await this.getIdToken();
  const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
  return this.http.post(this.toggleUserStatusUrl, { uid, companyId, isActive }, { headers }).toPromise();
}

// Método para limpiar IndexedDB
private async clearFirebaseIndexedDB(): Promise<void> {
  return new Promise((resolve) => {
    const databases = ['firebaseLocalStorageDb', 'firebase-installations-database'];
    let completedDeletions = 0;

    databases.forEach(dbName => {
      const deleteRequest = window.indexedDB.deleteDatabase(dbName);

      deleteRequest.onerror = () => {
        console.error(`Error deleting ${dbName}`);
        completedDeletions++;
        if (completedDeletions === databases.length) resolve();
      };

      deleteRequest.onsuccess = () => {
        console.log(`${dbName} successfully deleted`);
        completedDeletions++;
        if (completedDeletions === databases.length) resolve();
      };
    });

    // Si por alguna razón no se completa en 2 segundos, resolver de todos modos
    setTimeout(resolve, 2000);
  });
}

// Método para obtener el usuario con roles como Observable
getUserWithRoles(): Observable<CustomUser | null> {
  return this.currentUserWithRoles.asObservable();
}

// Método para obtener el usuario actual con roles
getCurrentUserWithRoles(): CustomUser | null {
  return this.currentUserWithRoles.value;
}

// Método para actualizar los datos del usuario con roles
private async updateUserWithRoles(firebaseUser: User): Promise<void> {
  try {
    // Primero intentar obtener del sessionStorage
    const storedUser = sessionStorage.getItem('currentUser');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      if (userData.roles) {
        this.currentUserWithRoles.next(userData as CustomUser);
        return;
      }
    }

    // Si no hay datos en sessionStorage o no tienen roles, obtener del token
    const token = await firebaseUser.getIdToken();
    const decodedToken = this.decodeJWT(token);
    
    const customUser: CustomUser = {
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || firebaseUser.email || '',
      token: token,
      roles: decodedToken?.roles || [],
      uid: firebaseUser.uid,
      // Añadir otras propiedades necesarias de CustomUser
      isActive: true,
      emailVerified: firebaseUser.emailVerified,
      isAnonymous: firebaseUser.isAnonymous,
      metadata: {
        creationTime: firebaseUser.metadata.creationTime || ''
      },
      providerData: firebaseUser.providerData,
      refreshToken: firebaseUser.refreshToken,
      tenantId: firebaseUser.tenantId,
      delete: firebaseUser.delete,
      getIdToken: firebaseUser.getIdToken,
      getIdTokenResult: firebaseUser.getIdTokenResult,
      reload: firebaseUser.reload,
      toJSON: firebaseUser.toJSON,
      phoneNumber: firebaseUser.phoneNumber,
      photoURL: firebaseUser.photoURL,
      providerId: '',
      identification: decodedToken?.identification
    };
    
    // Actualizar sessionStorage con los roles
    const sessionData = {
      email: customUser.email,
      roles: customUser.roles,
      token: token,
      displayName: customUser.displayName
    };
    sessionStorage.setItem('currentUser', JSON.stringify(sessionData));
    
    // Actualizar el BehaviorSubject
    this.currentUserWithRoles.next(customUser);
  } catch (error) {
    console.error('Error al actualizar datos del usuario con roles:', error);
  }
}

// Método para verificar si el usuario tiene un rol específico
hasRole(role: string): boolean {
  const user = this.currentUserWithRoles.value;
  return user !== null && user.roles && user.roles.includes(role) || false;
}

// Método para verificar si el usuario tiene al menos uno de los roles especificados
hasAnyRole(roles: string[]): boolean {
  const user = this.currentUserWithRoles.value;
  return user !== null && user.roles && roles.some(role => user.roles! && user.roles.includes(role)) || false;
}

// Método para decodificar el token JWT
private decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decodificando JWT:', error);
    return null;
  }
}

}